/**
 * HL-12029 — pop-up that re-asks one questionnaire question from the Health Summary
 * (arm B). The host renders the question's answer controls as `children` with the
 * questionnaire's own handlers, so selection / sibling / exclusive-none behaviour is the
 * page-per-question one; this component only owns the card chrome and the
 * Speichern / Schließen pair. The host snapshots the answers on open and restores them on
 * `onClose`, so closing discards every change (AC-11) and only `onSave` keeps them (AC-4).
 *
 * No dedicated Figma frame yet (ticket: "Answer pop-up — TBD"); the chrome follows the
 * health profile modal (7256:75114).
 */

import React from "react";
import * as translator from "../../../utils/translator";
import VariantBModalShell from "./VariantBModalShell";
import { useFunnel, useShellIdFn } from "./shellContext";
import PrimaryCtaButton from "./PrimaryCtaButton";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

interface AnswerEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  /** Question text (admin HTML) shown as the modal title. */
  titleHtml?: string;
  language?: string;
  children: React.ReactNode;
}

export const ANSWER_EDIT_MODAL_ID = "wl2_answer_edit_modal";

const AnswerEditModal: React.FC<AnswerEditModalProps> = ({ isOpen, onClose, onSave, titleHtml, language, children }) => {
  // Portaled sibling of the page body, so it mounts once; scoping keeps the id contract uniform.
  const scopeId = useShellIdFn();
  // HL-12357: WL3 slides the edit pop-up in.
  const isWl3 = useFunnel() === "wl3";
  return (
    <VariantBModalShell
      modalId={ANSWER_EDIT_MODAL_ID}
      isOpen={isOpen}
      onClose={onClose}
      maxWidthPx={480}
      shouldBackgroundClickHideModal={false}
      mobileBottomSheet
      animateIn={isWl3}
    >
      <h2
        id={scopeId(`${ANSWER_EDIT_MODAL_ID}_title`)}
        className="font-['Poppins'] font-medium text-[18px] leading-[1.3] text-[#08080a] m-0 pr-8 mb-6"
        dangerouslySetInnerHTML={{ __html: titleHtml || "" }}
      />

      <div className="flex flex-col gap-4 mb-6">{children}</div>

      <div className="flex flex-col gap-2 w-full">
        <PrimaryCtaButton id={`${ANSWER_EDIT_MODAL_ID}_save`} label={t(language, "health_summary_edit_save")} onClick={onSave} />
        <button
          id={scopeId(`${ANSWER_EDIT_MODAL_ID}_cancel`)}
          type="button"
          onClick={onClose}
          className="w-full h-[54px] rounded-[16px] bg-white border border-solid border-[#f08e00] text-[#f08e00] font-['Poppins'] font-semibold text-base cursor-pointer hover:opacity-90 active:scale-[0.96] transition-all duration-100"
        >
          {t(language, "health_summary_edit_close")}
        </button>
      </div>
    </VariantBModalShell>
  );
};

export default AnswerEditModal;
